'use client';
import { bg100 } from '@osmosis/tailwind-ui/styles/background';
import { textGray } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Card } from '@osmosis/tailwind-ui/components/Card';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P } from '@osmosis/tailwind-ui/components/Text';

import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faEdit } from '@fortawesome/pro-regular-svg-icons/faEdit';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons/faTrashAlt';

import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { isVisibleInParent, scrollToVisible } from '../../../lib/dom/scroll';
import FroalaContent from '../../FroalaContent';
import { clickable } from '../../../styles/hover';
import { formatSecondsToDuration } from '@osmosis/shared/video';
import { LearnNotesProps } from '@osmosis/shared/types/learn';

import dynamic from 'next/dynamic';

const CKEditor = dynamic(() => import('../../CKEditor'), { ssr: false });

export type Props = {
  note: LearnNotesProps;
  onDelete: (noteID: number) => Promise<void>;
  onClick: (note: LearnNotesProps) => void;
  onEdit: (note: LearnNotesProps, content: string) => Promise<void>;
  parentContainerRef?: MutableRefObject<HTMLDivElement | null>;
};

/**
 * Learn notes item.
 */
const LearnNotesItem = (props: Props) => {
  const { note, onEdit, onDelete, onClick, parentContainerRef } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(note.contents);
  const theme = useTheme();
  const containerRef = useRef(null);

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void onDelete(note.noteID);
  };
  const handleToggleEdit = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsEditing(!isEditing);
  };

  const handleSave = (): void => {
    setIsEditing(!isEditing);
    void onEdit(note, value);
  };

  const canClick = !!note.time && !isEditing;

  useEffect(() => {
    if (
      isEditing &&
      !isVisibleInParent(parentContainerRef?.current, containerRef.current)
    ) {
      // Scroll to appropriate location if not fully visible
      scrollToVisible(parentContainerRef?.current, containerRef.current, {
        paddingTop: theme?.space[1],
      });
    }
  }, [isEditing]);

  return (
    <Card
      ref={containerRef}
      style={{
        ...(canClick ? clickable('bg-0', 'bg-1') : {}),
      }}
      onClick={() => (canClick ? onClick?.(note) : null)}
      data-testid="learn-notes-item-card"
      className={`p-0 `}
    >
      <Box>
        {!isEditing && <FroalaContent content={note.contents} />}
        <Box className={isEditing ? 'block' : ''}>
          <CKEditor
            data={value}
            onChange={(e, editor) => {
              setValue(editor.getData());
            }}
          />
        </Box>
      </Box>

      <Flex className={`justify-between items-center p-4`}>
        <Box>
          {!!note.time && (
            <Box className={`${bg100} rounded-[8px] p-1`}>
              <P className={`text-[12px]`}>
                {formatSecondsToDuration(note.time)}
              </P>
            </Box>
          )}
        </Box>
        <Flex>
          {!isEditing && (
            <IconButton
              variant="transparent"
              onClick={handleToggleEdit}
              icon={<Icon icon={faEdit} />}
              data-testid="learn-notes-item_show-edit"
              className={`mr-2 text-[14px] ${textGray}`}
            />
          )}

          {isEditing && (
            <>
              <IconButton
                variant="transparent"
                onClick={handleSave}
                icon={<Icon icon={faCheck} />}
                data-testid="learn-notes-item-save-btn"
                className={`mr-2 text-[14px] text-green-600 dark:text-green-300`}
              />

              <IconButton
                variant="transparent"
                onClick={handleToggleEdit}
                icon={<Icon icon={faTimes} />}
                data-testid="learn-notes-item_close-edit"
                className={`mr-2 text-[14px] ${textGray}`}
              />
            </>
          )}

          <IconButton
            variant="transparent"
            onClick={handleDelete}
            icon={<Icon icon={faTrashAlt} />}
            data-testid="learn-notes-item_delete-btn"
            className={`text-[14px] text-red-500 dark:text-red-300`}
          />
        </Flex>
      </Flex>
    </Card>
  );
};

export default LearnNotesItem;
